import { React, useState, useEffect, useMemo, useRef, memo, Suspense } from "react";
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Avatar, Button, Link, Typography, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, FormControl, Select, Menu, MenuItem, InputLabel, Card, CardContent, alpha, useTheme, Grid, Tooltip } from '@mui/material';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from "react-router-dom";
import { useTabNavigation } from "../../hooks/useTabNavigation";
import TinyBarChart from "../charts/TinyBarChart";
import { PieChart } from '@mui/x-charts/PieChart';
import { PageHeader } from '../../shared/components/PageHeader';
import { CompactMetricCard } from '../../shared/components/CompactMetricCard';
import { CompactStatCard } from '../../shared/components/CompactStatCard';
import { MetricDashboardDialog } from '../../shared/components/MetricDashboardDialog';
import { ClientMetricsDialog } from './ClientMetricsDialog';
import { buildRotatingColorMap } from './metricsPanels';
import HotelIcon from '@mui/icons-material/Hotel';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import {
  ClientsTitleContainer,
  CrossImg,
  EmptyMessage,
  FilterChip,
  FilterChipList,
  HeaderContainer,
  NoteContainer,
  NoteDate,
  NoteHeader,
  NoteName,
  NoteText,
  NotesCardStatus,
  SearchInput,
  SearchInputContainer,
  ServiceButtons,
  ServiceContainer,
  ServiceDate,
  ServiceHeader,
  ServiceName,
  ServiceStatus,
  TableControlsContainer,
  TableSearchContainer,
  ToolsImgWhite,
  Img,
  AddNoteIcon,
} from './TableTab.styled';
import {
  AddItemContainer,
  AddItemOption,
  AddItemOptionsContainer,
  CalendarContainer,
  CalendarHeader,
  CalendarToolbar,
  CalendarToolsContainer,
  CheckboxContainer,
  CheckboxSubItems,
  DatePickerContainer,
  DateRangeContainer,
  EventClient,
  EventContainer,
  EventContent,
  EventFooter,
  EventHeader,
  EventMaterials,
  EventName,
  EventRequestSatatus,
  EventStatus,
  FilterGroup,
  PopupBody,
  PopupButton,
  RangePickerContainer,
  RangePickerItem,
  ShowMoreButton,
} from "../calendar/Calendar.styled";
import { NoteAddRounded, Search, WarningAmber, Sync as SyncIcon } from '@mui/icons-material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import { TableFilter } from './tableFilter/TableFilter';
import { useGetClientsMutation, useGetNotesTypesQuery, useGetProfilePictureQuery, useGetAssignmentClientsMutation, useEditNoteMutation, usePostNoteMutation, useCreateClientServiceMutation, useUpdateClientServiceMutation, useDeleteClientServiceMutation, useGetClientServiceByServiceIdQuery, useGetClientServiceHistoryQuery, useGetUserInfoQuery, useGetRolePermissionsQuery } from '../../services/ClientService';
import styled from '@emotion/styled';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingPanel } from '../../shared/components/LoadingPanel';
import { useGetClientCounselorsQuery, useGetClientHousingsQuery, useGetClientStatusesQuery, useGetCompanyBranchesQuery, useGetTracksQuery, useGetLevelOfCareQuery, useGetServicesDetailQuery } from '../../services/UtilityService';
import addClientSvg from "../../shared/icons/addClient.svg";
import crossSvg from "../../shared/icons/cross.svg";
import list from "../../shared/icons/list.svg";
import exportIcon from "../../shared/icons/export.svg";
import calendar from "../../shared/icons/calendar.svg";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import addNote from "../../shared/icons/addNote.svg";
import { setStatus, setCounselor, setHousing, setLevelofCare } from '../../store/slices/FilterSlice';
import useFetchWithMsal from '../../utils/useFetchWithMsal';
import { NotesPanel } from "./notesPanel/NotesPanel";
import { ServicesStatisticsPopup } from "./servicesStatisticsPopup/ServicesStatisticsPopup";
import { lazyNamed, useHasOpened } from "../../utils/lazyWithRetry";
import { NotesGridField } from "./notesPanel/NotesGridField";
import { SessionInfoDialog } from "../calendar/SessionInfoDialog";
import { useSessionServiceActions } from "../calendar/useSessionServiceActions";
import { ServiceHistoryDialog } from "../common/ServiceHistoryDialog";
import { MedicalServicesRounded, CheckCircle, CloseRounded, HourglassBottomRounded, DeleteOutline, AdminPanelSettings, Eject, Block, HourglassEmpty, History, HelpOutline, SentimentDissatisfied, PersonAdd, AttachMoney, EditOutlined, DescriptionOutlined, EventAvailableOutlined, ReceiptOutlined, CalendarMonthOutlined } from '@mui/icons-material';
import dayjs from "dayjs";
import Skeleton from "@mui/material/Skeleton";
import { Container } from "react-bootstrap";
import { height } from "@mui/system";

// Closed-by-default dialogs/drawers load on first open instead of with the page. The notes
// calendar and the legacy service-detail dialog pulled the whole Calendar and the assessment
// form (with the PDF renderer) into the first load of the Clients page.
const CalendarModalWrapper = lazyNamed(() => import("../calendar/CalendarModalWrapper"));
const ServiceEditDrawer = lazyNamed(() => import("../services/ServiceEditDrawer"), "ServiceEditDrawer");
const AddNotePopup = lazyNamed(() => import("../calendar/addServicesPopup/AddNotePopup"), "AddNotePopup");
const CreateClientTab = lazyNamed(() => import("../createClientTab/CreateClientTab"), "CreateClientTab");
const DetailPanelContent = lazyNamed(() => import("../bilingReport/detailPanelContent/DetailPanelContent"), "DetailPanelContent");


const StyledPaper = styled(Paper)(() => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
}));

// Inherits its size/font/weight/case straight from the app's MuiButton theme default
// (App.jsx) — the same look used by Calendar and Progress Notes' toolbar buttons — so it
// only needs to add its own spacing/wrapping.
export const CustomButton = styled(Button)(() => ({
  whiteSpace: 'nowrap',
  marginRight: '13px',
}));

// Service color mapping (mirrors billing report colors for consistency)
const serviceColors = {
  "Intake Assessment": "#FFA07A",
  "History and Physical": "#20B2AA",
  "Medical Progress Note": "#9370DB",
  "Nursing Detox": "#FF4500",
  "Partial Hospitalization Group therapy": "#4682B4",
  "Intensive Outpatient Group Therapy": "#32CD32",
  "Traditional Outpatient group therapy": "#FFD700",
  "3.1 Residential per diem": "#8B0000",
  "3.1 Room and Board": "#008080",
  "Individual Therapy": "#FF69B4",
  "Crisis Intervention": "#00CED1",
  "Case Management - Recovery Support": "#6A5ACD",
  "Urine screen": "#D2691E",
  "Family Therapy with Client": "#40E0D0",
  "Family Therapy without Client": "#DC143C",
  "Clinical update": "#7CFC00",
  "Vivitrol Injection": "#DAA520",
  "Substance Abuse Group Session": "#6495ED",
  "Substance Abuse Individual Session": "#BA55D3",
  "Discharge": "#2E8B57",
};

// Status colors for consistent visualization
const statusColors = {
  "Active": "#4CAF50",
  "None": "#9E9E9E",
  "Administrative Discharged": "#FF9800",
  "Discharged": "#F44336",
  "Pending": "#2196F3",
  "Intake": "#00BCD4",
  "On Hold": "#FFC107",
  "Transferred": "#9C27B0",
  "Deceased": "#424242",
  "Completed": "#8BC34A",
};

// Level of Care header cards: one stat tile per group rather than a donut, so a specific census
// (e.g. "3.1 Residential: 14") reads at a glance instead of requiring a click-through. The two
// 3.1 tracks (male/female units) are combined since they're the same level of care clinically;
// "Not Assigned"/"Unknown" is excluded since it isn't a level of care. LEVEL_OF_CARE_ORDER pins
// the known labels to a stable order; anything else (a level of care added later) sorts after,
// alphabetically, so the row stays complete without a code change.
const LEVEL_OF_CARE_ORDER = ['3.1 Residential', 'IOP', 'PHP', 'TOP'];
const LEVEL_OF_CARE_ICONS = {
  '3.1 Residential': HotelIcon,
  'IOP': GroupsOutlinedIcon,
  'PHP': MedicalServicesRounded,
  'TOP': EventAvailableOutlined,
};
// The two 3.1 tracks (male/female units) share one icon/group everywhere a level of care is
// shown — the header cards and the grid's Level of Care pill both go through this first.
const normalizeLevelOfCareLabel = (label) => (label === '3.1 - F' || label === '3.1 - M') ? '3.1 Residential' : label;
const getLevelOfCareIcon = (label) => LEVEL_OF_CARE_ICONS[normalizeLevelOfCareLabel(label)] || AssessmentIcon;

// Housing and Counselor colors come from the shared rotating palette (buildRotatingColorMap).

// Enhanced metric card component with donut chart
const MetricCard = ({ title, data, colorMap, icon: Icon }) => {
  const theme = useTheme();
  const cardColor = theme.palette.primary.main;

  const chartData = data.map((item, index) => ({
    id: index,
    value: item.value,
    label: item.label,
    color: colorMap[item.label] || theme.palette.grey[400],
  }));

  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card
      sx={{
        background: `linear-gradient(135deg, ${alpha(cardColor, 0.05)} 0%, ${alpha(cardColor, 0.15)} 100%)`,
        borderLeft: `4px solid ${cardColor}`,
        boxShadow: 2,
        transition: 'all 0.3s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Left: title, total badge, horizontal legend */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ bgcolor: alpha(cardColor, 0.1), borderRadius: 1.5, p: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon sx={{ fontSize: 16, color: cardColor }} />
            </Box>
            <Typography fontWeight={600} sx={{ fontSize: '0.78rem', lineHeight: 1.2 }}>{title}</Typography>
          </Box>
          <Typography sx={{
            fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary',
            bgcolor: alpha(cardColor, 0.1), px: 1, py: 0.25, borderRadius: 1, alignSelf: 'flex-start',
          }}>
            {total} Total
          </Typography>
          {/* Horizontal legend */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '3px 10px' }}>
            {data.map((item) => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colorMap[item.label] || '#ccc', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {item.label} ({item.value})
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Right: donut chart only, no built-in legend */}
        <Box sx={{ flexShrink: 0 }}>
          <PieChart
            series={[{
              data: chartData,
              innerRadius: 30,
              outerRadius: 55,
              paddingAngle: 2,
              cornerRadius: 3,
              highlightScope: { faded: 'global', highlighted: 'item' },
              faded: { innerRadius: 26, additionalRadius: -4, color: 'gray' },
            }]}
            width={120}
            height={120}
            slots={{ legend: () => null }}
            slotProps={{ legend: { hidden: true } }}
            margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
            sx={{
              '& .MuiPieArc-root text': { fill: 'white', fontWeight: 600, fontSize: '10px' },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};




// Detail-panel building blocks: a bordered card with an icon/title header and optional
// top-right quick-action links, used for every section (Insurance, Contact, Housing,
// Counselor, Services, Notes & Alerts) inside an expanded client row.
const DetailSection = ({ icon: Icon, title, actions, children }) => (
  <Box
    sx={{
      bgcolor: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3,
      p: 1.75,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 0.5, mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {Icon && <Icon sx={{ fontSize: 16, color: 'text.secondary' }} />}
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary' }}>
          {title}
        </Typography>
      </Box>
      {actions && actions.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1.25 }}>
          {actions.map((a) => (
            <Typography
              key={a.label}
              onClick={a.onClick}
              sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main', cursor: 'pointer', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}
            >
              {a.label}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
    <Box sx={{ fontSize: 13 }}>{children}</Box>
  </Box>
);

const KeyValueRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: '2px' }}>
    <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>{label}</Typography>
    <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 600, textAlign: 'right' }}>{value || '—'}</Typography>
  </Box>
);

// Module scope on purpose: defined inside TableTab it was a new component type on every
// render, so the grid remounted every avatar whenever anything on the page changed.
const ProfilePictureCell = memo(({ clientId, name, size = 64, refreshToken = 0 }) => {
  const { data: pictureUrl, isLoading, refetch } = useGetProfilePictureQuery({ id: clientId, resolution: "thumbnail" });

  // The grid's Refresh button bumps refreshToken. Remounting alone reused the cached picture,
  // so a photo uploaded since the grid loaded never appeared; re-fetch instead. The API answers
  // an unchanged photo with a 304, so this stays cheap. Skipped on mount (e.g. paging after a
  // refresh) so only an actual Refresh click triggers it.
  const lastRefreshToken = useRef(refreshToken);
  useEffect(() => {
    if (refreshToken !== lastRefreshToken.current) {
      lastRefreshToken.current = refreshToken;
      refetch();
    }
  }, [refreshToken, refetch]);

  if (isLoading) {
    return <Skeleton variant="circular" width={size} height={size} animation="wave" />;
  }

  // Initials fallback so clients without a photo still render a clean avatar (not a broken image).
  const initials = (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("") || "?";

  return (
    <Avatar
      src={pictureUrl || undefined}
      alt={name || "Client"}
      sx={{
        width: size,
        height: size,
        bgcolor: "primary.main",
        color: "#fff",
        fontSize: size >= 48 ? 22 : 13,
        fontWeight: 700,
        border: "2px solid #fff",
        boxShadow: "0 1px 4px rgba(16,42,74,0.18)",
        flexShrink: 0,
      }}
    >
      {initials}
    </Avatar>
  );
});

// Avatar for the recent-services session popup (mirrors Calendar's ClientAvatar).
const SessionClientAvatar = ({ clientId: cid }) => {
  const { data: pictureUrl } = useGetProfilePictureQuery({ id: cid, resolution: 'thumbnail' }, { skip: !cid });
  return (
    <Avatar src={pictureUrl || undefined} sx={{ width: 28, height: 28 }}>
      <PersonIcon sx={{ fontSize: 18 }} />
    </Avatar>
  );
};

export const TableTab = () => {
  const dispatch = useDispatch();
  const { skip } = useFetchWithMsal();
  const { openClient, openClientAtTab, openService, openPage, openCalendarForClient } = useTabNavigation();

  // Role-based filtering for Services (Form permissions)
  const { data: userInfo } = useGetUserInfoQuery(undefined, { skip });
  const currentRoleId = Number(userInfo?.roleId ?? userInfo?.RoleId ?? 0);
  const currentUserId = String(userInfo?.userId ?? userInfo?.UserId ?? userInfo?.id ?? userInfo?.Id ?? '');
  // Only roleId=22 (management role) bypasses service filtering
  const isManagementRole = Number(currentRoleId) === 22;
  const shouldEnforceRolePermissions = Boolean(currentRoleId) && !isManagementRole;

  const {
    data: rolePermissionsRaw = [],
    isLoading: isRolePermissionsLoading,
  } = useGetRolePermissionsQuery(
    { roleId: currentRoleId, userId: currentUserId },
    { skip: skip || !shouldEnforceRolePermissions || !currentUserId }
  );

  const allowedFormLinkIds = useMemo(() => {
    if (!shouldEnforceRolePermissions) return null;
    const arr = Array.isArray(rolePermissionsRaw) ? rolePermissionsRaw : [];
    const set = new Set();
    arr.forEach((p) => {
      const permissionTypeId = Number(p?.permissionTypeId ?? p?.PermissionTypeId ?? 0);
      const linkId = String(p?.linkId ?? p?.LinkId ?? '');
      const accessLevelId = Number(p?.permissionAccessLevelId ?? p?.PermissionAccessLevelId ?? 0);
      if (permissionTypeId === 2 && linkId && accessLevelId >= 1) {
        set.add(linkId);
      }
    });
    return set;
  }, [rolePermissionsRaw, shouldEnforceRolePermissions]);

  const filterRowServices = useMemo(() => {
    return (services) => {
      const arr = Array.isArray(services) ? services : [];
      if (isManagementRole) return arr;
      if (!shouldEnforceRolePermissions) return arr;
      // While loading permissions, default-deny to avoid flashing restricted services
      if (isRolePermissionsLoading) return [];
      const allowed = allowedFormLinkIds;
      if (!allowed) return arr;
      return arr.filter((s) => {
        const serviceId = String(s?.serviceId ?? s?.ServiceId ?? s?.id ?? s?.Id ?? '');
        return allowed.has(serviceId);
      });
    };
  }, [allowedFormLinkIds, isRolePermissionsLoading, shouldEnforceRolePermissions, isManagementRole]);

  // Default sort to CreatedDate Descending per new control
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('CreatedDate');
  const [sortSelect, setSortSelect] = useState('CreatedDateDesc');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [counselorFilter, setCounselorFilter] = useState(null);
  const [housingFilter, setHousingFilter] = useState(null);
  const [levelofcareFilter, setLevelofCareFilter] = useState(null);
  const [openNotesPanel, setOpenNotesPanel] = useState(false);
  const [notes, setNotes] = useState([]);
  // When a note is clicked in the grid, open the notes drawer focused on that note.
  const [initialEditNote, setInitialEditNote] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientsType, setClientsType] = useState('assigned');
  const initialStartDate = dayjs((new Date().setMonth(new Date().getMonth())));
  const initialEndDate = dayjs((new Date().setMonth(new Date().getMonth())));
  const [dateFrom, setDateFrom] = useState(initialStartDate);
  const [dateTo, setDateTo] = useState(initialEndDate);

  const [selectedUserName, setSelectedUserName] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [serviceCalendarOpen, setServiceCalendarOpen] = useState(false);
  const [notesCalendarOpen, setNotesCalendarOpen] = useState(false);

  const [servicesStatisticsOpen, setServicesStatisticsOpen] = useState(false);

  const [isAddItemDropdownVisible, setIsAddItemDropdownVisible] = useState(false);
  const [isShowAddServicePopup, setIsShowAddServicePopup] = useState(false);
  const [isShowEditNotePopup, setIsShowEditNotePopup] = useState(false);
  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [editNote, { isLoading: isEditNodeLoading }] = useEditNoteMutation();
  const [postNote, { isLoading: isPostNodeLoading }] = usePostNoteMutation();
  const [addedNote, setAddedNote] = useState(null);

  const [addedService, setAddedService] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceClickKey, setServiceClickKey] = useState(0);
  const [changebleEvent, setChangebleEvent] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const [pendingType, setPendingType] = useState('');
  const [postClientService, { isLoading: isEditServiceLoading }] = useCreateClientServiceMutation();
  const [updateClientService, { isLoading: isPostServiceLoading }] = useUpdateClientServiceMutation();
  const [deleteClientService] = useDeleteClientServiceMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  // Service info popup (replicates the calendar's compact event dialog)
  const [selectedGridEvent, setSelectedGridEvent] = useState(null); // { service, row }
  // Incremented by Refresh; each ProfilePictureCell re-fetches its picture when it changes.
  const [picRefreshKey, setPicRefreshKey] = useState(0);


  const { status, counselor, housing, levelofcare } = useSelector(
    (state) => state.filter
  );

  const { data: statues } = useGetClientStatusesQuery('', { skip });
  const { data: counselors } = useGetClientCounselorsQuery('', { skip });
  const { data: housings } = useGetClientHousingsQuery('', { skip });
  const { data: types } = useGetNotesTypesQuery({ skip });
  const { data: levelofcares } = useGetLevelOfCareQuery('', { skip });

  // Authoritative all-day flag per service type (Service.IsAllDay) so the service-info popup
  // shows "All day" instead of a timezone-fragile midnight time — matching the calendar.
  const { data: servicesDetail } = useGetServicesDetailQuery(undefined, { skip });
  const serviceAllDayMap = useMemo(() => {
    const m = {};
    (servicesDetail ?? []).forEach((s) => { m[String(s.id ?? s.Id)] = !!(s.isAllDay ?? s.IsAllDay); });
    return m;
  }, [servicesDetail]);

  // Optimistic overrides (clientServiceId → patch) for the recent-services popup, so an
  // attendance/mark-performed change shows immediately without a refetch.
  const [gridServicePatch, setGridServicePatch] = useState({});
  const {
    attendanceOptions: gridAttendanceOptions,
    onSetAttendance: gridOnSetAttendance,
    onMarkPerformed: gridOnMarkPerformed,
    markPerformedBusyId: gridMarkPerformedBusyId,
    performWarningDialog: gridPerformWarning,
  } = useSessionServiceActions({
    patchClient: (id, patch) => setGridServicePatch((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } })),
  });


  const [getAssignmentClients, { data: assignmentClients }] = useGetAssignmentClientsMutation({
    isShowAll: clientsType === 'all' ? true : false
  }, { skip })

  const [getClients, { data, isLoading, isError, error: clientsError }] = useGetClientsMutation({
    recordNumber: rowsPerPage,
    offset: page * rowsPerPage,
    isShowAll: clientsType === 'all' ? true : false
  }, { skip })


  const handleClientsTypeChange = (event, type) => {
    setClientsType(type);
  };

  // The assignment-client list only feeds the Create-Service drawer and Add-Note popup
  // (both closed on first paint). Defer this heavy full-list fetch until one of those is
  // actually opened so it doesn't compete with the roster load on first login. The mutation
  // caches its result, so reopening is instant; toggling assigned/all still refetches because
  // the popup is open when clientsType changes.
  useEffect(() => {
    if (!skip && (isShowAddServicePopup || isShowEditNotePopup)) {
      getAssignmentClients({
        startDate: null,
        endDate: null,
        isShowAll: clientsType === 'all' ? true : false
      });
    }
  }, [dateFrom, dateTo, clientsType, skip, isShowAddServicePopup, isShowEditNotePopup]);

  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState("");

  let originalRows = data?.clients

  useEffect(() => {
    if (originalRows) {
      setRows(originalRows)
    }
    const total = data?.metrics?.totalAll ?? data?.totalCount;
    if (total != null) {
      setCount(total)
    }
  }, [data, originalRows]);




  // NOTE: counselorFilter/housingFilter have no setters wired up (dead state), so this effect
  // only ever fired once on mount and issued a duplicate GetClientsByFilter identical to the
  // canonical fetch effect below — loading the roster twice on every first login. Removed; the
  // page/sort/search effect below is the single source of truth for the initial + refetch loads.
  // Interactive counselor/housing chip filtering still runs through onApplyRowFilter directly.

  const replaceName = (name) => {
    if (name === 'firstName') {
      return 'fullName';
    } else if (name === 'counselorFirstName') {
      return 'counselorFullName';
    } else {
      return name;
    }
  }

  useEffect(() => {
    if (!skip) {
      const transformedOrderBy = replaceName(orderBy)
      getClients({
        recordNumber: rowsPerPage,
        offset: page * rowsPerPage,
        status,
        counselor,
        housing,
        levelofcare,
        order,
        orderBy: transformedOrderBy,
        searchVal: searched,
      })
    }
  }, [page, rowsPerPage, order, orderBy, skip, searched]);

  // Column header sorting disabled; sorting handled exclusively by select box

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.name);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  // Rename the returned variables to avoid conflict
  const {
    data: serviceData,
    error: serviceError,
    isLoading: isServiceLoading,
  } = useGetClientServiceByServiceIdQuery(selectedServiceId, {
    skip: !selectedServiceId || selectedServiceId === 0, // Skip if no serviceId is selected
  });

  // The grid's `service` objects are a lean summary (id/name/status/startDate only), so the
  // session popup was missing provider, times, attendance, etc. Fetch the full record on click
  // to match the calendar's popup. Separate from selectedServiceId so the legacy detail dialog
  // (used by the intake flow) is not triggered.
  const clickedGridServiceId = selectedGridEvent?.service?.id ?? null;
  const { data: gridServiceDetail } = useGetClientServiceByServiceIdQuery(clickedGridServiceId, {
    skip: !clickedGridServiceId,
  });

  // Status history for the clicked grid service (parity with the calendar popup's history button).
  const [statusHistoryServiceId, setStatusHistoryServiceId] = useState(null);
  const {
    data: gridStatusHistory = [],
    isFetching: isGridHistoryLoading,
    isError: isGridHistoryError,
  } = useGetClientServiceHistoryQuery(statusHistoryServiceId, { skip: !statusHistoryServiceId });

  /*   const RecentServicesOnClick = (event) => {
      console.log('RecentServicesOnClick event:', event);
      // Reset selectedServiceId to null before setting it to the new value to allow reselecting the same service
      setSelectedServiceId(event.id);
      setServiceClickKey((prevKey) => prevKey + 1); // Update the click key to force useEffect to run
    }; */

  const handleServiceClick = (clientId, service, row) => {
    // Close any stale detail panel before opening the new session dialog
    setIsDetailPanelOpen(false);
    // Open the compact service info popup (mirrors the calendar event dialog)
    setSelectedGridEvent({ service, row });
  };

  const handleCloseDetailPanel = () => {
    setIsDetailPanelOpen(false);
    setSelectedService(null);
    setSelectedServiceId(null);
  };


  const navigate = useNavigate();

  useEffect(() => {
    if (serviceData) {
      console.log('RecentServicesOnClick serviceData:', serviceData);
      console.log('RecentServicesOnClick selectedServiceId:', selectedServiceId);
      console.log('RecentServicesOnClick form ULR:', serviceData.psychosocialAssessmentForm);
      // Set client name and client ID with fallback to empty strings if undefined
      setClientName(`${serviceData.clientFirstName || ""} ${serviceData.clientLastName || ""}`);
      setClientId(serviceData.clientId || null);

      // Creating data to set with safeguards for each field
      const dataToSet = {
        startDate: serviceData.startDate ? new Date(serviceData.startDate).toISOString() : null,
        endDate: serviceData.endDate ? new Date(serviceData.endDate).toISOString() : null,
        date: serviceData.startDate ? new Date(serviceData.startDate).toISOString() : null,
        description: serviceData.description || "",
        materials: serviceData.materials || "",
        id: serviceData.id || null,
        serviceId: serviceData.serviceId || null,
        clientId: serviceData.clientId || null,
        serviceGroupNameId: serviceData.serviceGroupNameId || null,
        serviceTopicId: serviceData.serviceTopicId || null,
        serviceRoomId: serviceData.serviceRoomId || null,
        attendanceStatus: serviceData.attendanceStatus || "",
        trackId: serviceData.trackId || null,
        note: serviceData.note || "",
        psychosocialAssessmentForm: serviceData.psychosocialAssessmentForm || "",
      };

      // Open the intake form in a tab (matches the calendar), not a dialog.
      setSelectedUserId(serviceData.clientId || null);
      const cName = `${serviceData.clientFirstName || ''} ${serviceData.clientLastName || ''}`.trim();
      const serviceName = serviceData.name || serviceData.description || 'Intake Assessment';
      const tabTitle = cName ? `${cName} - ${serviceName}` : serviceName;
      openService(
        serviceData.psychosocialAssessmentFormId || serviceData.serviceId,
        tabTitle,
        serviceData.clientId,
        serviceData.id,
        serviceData.psychosocialAssessmentFormVersion ?? null
      );
      // Unsubscribe so this effect doesn't re-fire (and re-open the form) when the same service's
      // detail refetches later — e.g. after editing/saving it (cache invalidation).
      setSelectedServiceId(null);
    }
  }, [serviceData, serviceClickKey]);

  const handleOpenNotes = (notes, id) => {
    setOpenNotesPanel(true);
    setClientId(id)
    setNotes(notes)
  }

  const handlePaginationModelChange = (model) => {
    if (model.pageSize !== rowsPerPage) {
      setRowsPerPage(model.pageSize);
      setPage(0);
    } else {
      setPage(model.page);
    }
  };

  const requestSearch = (searchVal) => {
    setSearched(searchVal);
    setPage(0);
    // The useEffect([..., searched]) handles the API call
  };

  const onApplyFilters = (resetFilter) => {
    console.log('ApplyFilters counselours', counselors);
    getClients({
      recordNumber: rowsPerPage,
      offset: 0,
      status: resetFilter === 'status' ? '' : status,
      counselor: resetFilter === 'counselor' ? '' : counselor,
      housing: resetFilter === 'housing' ? '' : housing,
      levelofcare: resetFilter === 'levelofcare' ? '' : levelofcare,
      order,
      orderBy: replaceName(orderBy)
    });
    setPage(0);
  };

  const onApplyRowFilter = (counselorFilter, housingFilter) => {
    setPage(0);
    getClients({
      recordNumber: rowsPerPage,
      offset: 0,
      counselor: counselorFilter,
      housing: housingFilter,
      order,
      orderBy: replaceName(orderBy)
    })
  };

  const onHousingRowFilterClick = (housingId) => {
    dispatch(setHousing(housingId))
    onApplyRowFilter('', housingId)
  }

  const onCounselorRowFilterClick = (counselorId) => {
    dispatch(setCounselor(counselorId))
    onApplyRowFilter(counselorId, '')
  }

  const onResetFilters = () => {
    getClients({
      recordNumber: rowsPerPage,
      offset: 0,
      order,
      orderBy: replaceName(orderBy)
    });
    setPage(0);
  };

  const onResetStatus = () => {
    dispatch(setStatus(""));
    onApplyFilters('status')
  };

  const onResetCounselor = () => {
    dispatch(setCounselor(""));
    onApplyFilters('counselor')
  };

  const onResetHousing = () => {
    dispatch(setHousing(""));
    onApplyFilters('housing')
  };

  const onResetLevelofCare = () => {
    dispatch(setLevelofCare(""));
    onApplyFilters('levelofcare')
  };

  const openServiceCalendar = (userId, userName) => {
    setServiceCalendarOpen(!serviceCalendarOpen)
    setSelectedUserId(userId)
    setSelectedUserName(userName)
  }

  const openNotesCalendar = (userId, userName) => {
    setNotesCalendarOpen(!notesCalendarOpen)
    setSelectedUserId(userId)
    setSelectedUserName(userName)
  }

  const closeServiceCalendar = () => {
    setServiceCalendarOpen(!serviceCalendarOpen)
  }

  const closeNotesCalendar = () => {
    setNotesCalendarOpen(!notesCalendarOpen)
  }

  const openStatistics = (userId, userName) => {
    setServicesStatisticsOpen(true)
    setSelectedUserId(userId)
    setSelectedUserName(userName)
  }

  const closeStatistics = () => {
    setServicesStatisticsOpen(false)
  }

  useEffect(() => {
    setIsAddItemDropdownVisible(false);
  }, [isShowAddServicePopup, isShowEditNotePopup]);

  const onPopupClose = () => {
    setIsShowAddServicePopup(false);
    setIsShowEditNotePopup(false);

    setSelectedServiceId(0);
    setChangebleEvent(null);
  };

  const onCreateNote = (data, type) => {
    if (type === "change") {
      editNote({
        data: {
          text: data.text,
          clientId: clientId,
          quickNoteTypeId: data.quickNoteTypeId,
          ownerId: data.ownerId,
          quickNoteRequestSatatusId: data.requestStatus || null,
        }, noteId: data.noteId
      }).then(() => setAddedNote(data));
    } else {
      postNote({
        text: data.text,
        clientId: clientId ? clientId : data.client,
        quickNoteTypeId: data.quickNoteTypeId,
        quickNoteRequestSatatusId: data.requestStatus || null,
      }).then(() => setAddedNote(data));
    }

    onPopupClose();
  };

  const onCreateServiceWrapper = (data, type) => {
    setPendingData(data);
    setPendingType(type);
    onCreateService(data, type);
  };

  const extractIds = array => {
    return array.map(item => item.id);
  }

  const onCreateService = (data, type) => {
    data.clients = data?.clientId?.length > 1 ? extractIds(data.clientId) : null;
    data.clientId = clientId ? clientId : data.clientId[0]?.id || data.clientId;
    data.isGroup = data?.clients?.length > 1 ? true : false;
    data.serviceRoomId = data?.serviceRoomId?.id;
    // Selected provider → owner. Empty/0 means "unchanged" (backend COALESCEs to the existing owner).
    data.ownerUserId = data.providerUserId ? data.providerUserId : null;

    // The drawer keeps the time-of-day in scheduledStartTime/scheduledEndTime ("HH:mm").
    // Map it to the model's startTime/endTime (naive local "HH:mm:ss") so it persists to the
    // StartTime/EndTime columns the calendar reads, and embed it into startDate/endDate so the
    // grid/popup (which read the datetime) also reflect the change.
    if (!data.allDay && data.scheduledStartTime) {
      data.startTime = `${data.scheduledStartTime}:00`;
      const startDay = dayjs(data.startDate).isValid() ? dayjs(data.startDate) : dayjs();
      data.startDate = `${startDay.format('YYYY-MM-DD')}T${data.scheduledStartTime}:00`;
      data.date = data.startDate;
    }
    if (!data.allDay && data.scheduledEndTime) {
      data.endTime = `${data.scheduledEndTime}:00`;
      const endDay = dayjs(data.endDate).isValid() ? dayjs(data.endDate)
        : (dayjs(data.startDate).isValid() ? dayjs(data.startDate) : dayjs());
      data.endDate = `${endDay.format('YYYY-MM-DD')}T${data.scheduledEndTime}:00`;
    }

    const handleResponse = (response) => {
      if (response?.data?.isConflict) {
        setModalMessage(response.data?.message);
        setModalOpen(true);
      } else {
        setAddedService(data);
        onPopupClose();
      }
    };

    if (type === "change") {
      updateClientService(data).then(handleResponse);
    } else {
      postClientService({ clientId, data }).then(handleResponse);
    }
  };

  // Hard-delete a service (and its linked family) from the edit drawer — mirrors the
  // calendar's delete so the option exists when editing from the client session popup.
  const handleDeleteServiceFromDrawer = async (values) => {
    const id = values?.id;
    if (!id) return;
    const res = await deleteClientService(id);
    const payload = res?.data;
    if (res?.error || (payload && payload.success === false)) {
      setModalMessage(payload?.message || res?.error?.message || 'Unable to delete this service.');
      setModalOpen(true);
      return;
    }
    onPopupClose();
    // The clients list is fetched via a mutation (no tag invalidation), so refetch to drop the row.
    await getClients({
      recordNumber: rowsPerPage,
      offset: page * rowsPerPage,
      status,
      counselor,
      housing,
      levelofcare,
      order,
      orderBy: replaceName(orderBy),
    });
  };

  // Create new Intake Assessment service (serviceId 1) and open its form
  const handleCreateIntakeService = async (clientRow) => {
    try {
      // The "Intake Not Complete" alert persists until the intake is finished, so if an
      // intake service (serviceId 1) already exists for this client, OPEN it instead of
      // creating another one — otherwise every click piles up duplicate intake records.
      const existingIntake = (Array.isArray(clientRow?.service) ? clientRow.service : [])
        .find((s) => Number(s?.serviceId ?? s?.ServiceId) === 1 && (s?.id ?? s?.Id));
      if (existingIntake) {
        setSelectedServiceId(existingIntake.id ?? existingIntake.Id);
        setServiceClickKey((prev) => prev + 1);
        return;
      }

      // Auto-populate with the user's *local* wall-clock time. toISOString() would
      // convert to UTC, shifting the displayed start/end by the timezone offset;
      // the rest of the app treats service dates as naive local datetimes.
      const now = dayjs().second(0).millisecond(0);
      // End time follows the Intake service's own default duration (matches the
      // Add Service drawer's auto-duration behavior) instead of collapsing to a
      // zero-length start==end appointment.
      const intakeServiceDetail = (servicesDetail ?? []).find((s) => Number(s?.Id ?? s?.id) === 1);
      const intakeDurationMins = Number(
        intakeServiceDetail?.defaultLengthMins ?? intakeServiceDetail?.DefaultLengthMins ?? intakeServiceDetail?.defaultDurationMinutes ?? 0
      );
      const end = intakeDurationMins > 0 ? now.add(intakeDurationMins, 'minute') : now;
      const nowLocal = now.format('YYYY-MM-DDTHH:mm:ss');
      const endLocal = end.format('YYYY-MM-DDTHH:mm:ss');
      // The time-of-day is stored in the StartTime/EndTime columns (naive local,
      // no UTC conversion) — that's what the calendar reads to render the event time.
      const nowTime = now.format('HH:mm:ss');
      const endTime = end.format('HH:mm:ss');
      const newService = {
        description: 'Intake Assessment',
        endDate: endLocal,
        startDate: nowLocal,
        startTime: nowTime,
        endTime: endTime,
        serviceId: 1,
        clientId: clientRow.id,
        serviceGroupNameId: 0,
        serviceTopicId: 0,
        serviceRoomId: 2,
        trackId: 0,
        note: ''
      };
      const response = await postClientService({ clientId: clientRow.id, data: newService });
      const createdId = response?.data;
      if (typeof createdId === 'number' && createdId > 0) {
        // Refresh the clients list before opening the form
        await getClients({
          recordNumber: rowsPerPage,
          offset: page * rowsPerPage,
          status,
          counselor,
          housing,
          levelofcare,
          order,
          orderBy: replaceName(orderBy)
        });
        setSelectedServiceId(createdId);
        setServiceClickKey((prev) => prev + 1);
      } else if (response?.data?.id) {
        await getClients({
          recordNumber: rowsPerPage,
          offset: page * rowsPerPage,
          status,
          counselor,
          housing,
          levelofcare,
          order,
          orderBy: replaceName(orderBy)
        });
        // Fallback in case API returns an object with id
        setSelectedServiceId(response.data.id);
        setServiceClickKey((prev) => prev + 1);
      }
    } catch (e) {
      console.error('Error creating Intake Assessment:', e);
    }
  };

  const levelOfCareCounts = useMemo(() => {
    // Use metrics from API response if available
    if (data?.metrics?.totalByLevelOfCare) {
      return data.metrics.totalByLevelOfCare;
    }
    // Fallback to calculating from rows
    const counts = {};
    rows.forEach((row) => {
      const loc = row.levelOfCareName || 'Unknown';
      counts[loc] = (counts[loc] || 0) + 1;
    });
    return counts;
  }, [data, rows]);

  const statusCounts = useMemo(() => {
    // Use metrics from API response if available
    if (data?.metrics?.totalByStatus) {
      return data.metrics.totalByStatus;
    }
    // Fallback to calculating from rows
    const counts = {};
    rows.forEach((row) => {
      const status = row.statusName || 'Unknown';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [data, rows]);

  // Grouped Level of Care totals: merge the two 3.1 tracks, drop Not Assigned/Unknown, order by
  // LEVEL_OF_CARE_ORDER with any unlisted label sorted alphabetically after the known ones.
  const levelOfCareTotals = useMemo(() => {
    const merged = {};
    Object.entries(levelOfCareCounts).forEach(([label, value]) => {
      if (label === 'Not Assigned' || label === 'Unknown') return;
      const key = normalizeLevelOfCareLabel(label);
      merged[key] = (merged[key] || 0) + value;
    });
    return Object.entries(merged).sort(([a], [b]) => {
      const ai = LEVEL_OF_CARE_ORDER.indexOf(a);
      const bi = LEVEL_OF_CARE_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [levelOfCareCounts]);

  const statusData = Object.entries(statusCounts).map(([label, value]) => ({
    label,
    value,
  }));

  // Housing/Counselor/Intake: prefer server metrics (computed over the FULL filtered set) the
  // same way Level of Care/Status already do above — this grid paginates server-side, so a
  // rows-only tally would only ever reflect whichever page happens to be loaded right now.
  const housingCounts = useMemo(() => {
    if (data?.metrics?.totalByHousing) return data.metrics.totalByHousing;
    const counts = {};
    rows.forEach((row) => {
      const housing = row.housingName || 'Not Assigned';
      counts[housing] = (counts[housing] || 0) + 1;
    });
    return counts;
  }, [data, rows]);
  const housingData = Object.entries(housingCounts).map(([label, value]) => ({ label, value }));
  const housingColors = useMemo(() => buildRotatingColorMap(Object.keys(housingCounts)), [housingCounts]);

  const counselorCounts = useMemo(() => {
    if (data?.metrics?.totalByCounselor) return data.metrics.totalByCounselor;
    const counts = {};
    rows.forEach((row) => {
      const name = `${row.counselorFirstName || ''} ${row.counselorLastName || ''}`.trim() || 'Unassigned';
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }, [data, rows]);
  const counselorData = Object.entries(counselorCounts).map(([label, value]) => ({ label, value }));
  const counselorColors = useMemo(() => buildRotatingColorMap(Object.keys(counselorCounts)), [counselorCounts]);

  const intakeStatusCounts = useMemo(() => {
    if (data?.metrics?.totalByIntakeStatus) return data.metrics.totalByIntakeStatus;
    const counts = { Complete: 0, Incomplete: 0 };
    rows.forEach((row) => {
      if (row.intakeDetails === 'Intake Not Complete') counts.Incomplete += 1;
      else counts.Complete += 1;
    });
    return counts;
  }, [data, rows]);

  // Expanded breakdown dialog — opens on click for any of the metric cards below, replacing the
  // old hover tooltip. metricDialog holds { title, data, colorMap } for whichever card was clicked.
  const [metricDialog, setMetricDialog] = useState(null);

  // "Add New" toolbar menu — consolidates Create Service / Add Note / Add Client into one button.
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);

  // Header metrics: only the two actionable/most-checked cards (Intake Not Complete, Status)
  // show in the header. Level of Care, Housing, and Counselor open in ClientMetricsDialog from
  // the "View metrics" button instead of crowding the header on every load.
  const [metricsDialogOpen, setMetricsDialogOpen] = useState(false);
  // Lazily loaded drawer/popup mount on first open, then stay mounted (keeps close animations).
  const serviceDrawerOpened = useHasOpened(isShowAddServicePopup);
  const notePopupOpened = useHasOpened(isShowEditNotePopup);

  // Standard grid columns (same DataGridPro pattern as the Manage Services grid): one line
  // per field, no cell ever grows past this. Everything else (insurance, phone, full service
  // history, notes & alerts) lives in the expandable detail card below.
  // Stable columns: rebuilt each render, the grid re-rendered every cell on any state change.
  // openClient comes from a hook and is not stable, so cells reach it through a ref.
  const openClientRef = useRef(openClient);
  openClientRef.current = openClient;
  const gridColumns = useMemo(() => [
    {
      field: 'client',
      headerName: 'Client',
      flex: 1.7,
      minWidth: 220,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const fullName = `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
        return (
          <Box
            onClick={(e) => { e.stopPropagation(); openClientRef.current(row.id, fullName); }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', cursor: 'pointer' }}
          >
            <ProfilePictureCell key={`pic-${row.id}`} clientId={row.id} name={fullName} size={48} refreshToken={picRefreshKey} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'primary.main', lineHeight: 1.3, '&:hover': { textDecoration: 'underline' } }} noWrap>
                {fullName}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>
                {`BBHH${String(row.id).padStart(7, '0')}`}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'statusName',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const statusName = params.row.statusName;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {statusName ? (
              <Box component="span" sx={{
                display: 'inline-flex', alignItems: 'center', height: 24, px: 1.1,
                borderRadius: '999px', bgcolor: alpha(statusColors[statusName] || '#9E9E9E', 0.14),
                color: statusColors[statusName] || '#9E9E9E',
                fontWeight: 700, fontSize: 11.5, whiteSpace: 'nowrap',
              }}>
                {statusName}
              </Box>
            ) : (
              <Box component="span" sx={{ color: 'text.disabled', fontSize: 13 }}>—</Box>
            )}
          </Box>
        );
      },
    },
    {
      field: 'levelOfCareName',
      headerName: 'Level of Care',
      flex: 0.9,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const loc = params.row.levelOfCareName;
        const LocIcon = getLevelOfCareIcon(loc);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {loc ? (
              <Box component="span" sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5, height: 24, px: 1.1,
                borderRadius: '999px', bgcolor: '#e3f2fd', color: '#1565c0',
                fontWeight: 700, fontSize: 11.5, whiteSpace: 'nowrap',
              }}>
                <LocIcon sx={{ fontSize: 14 }} />
                {loc}
              </Box>
            ) : (
              <Box component="span" sx={{ color: 'text.disabled', fontSize: 13 }}>—</Box>
            )}
          </Box>
        );
      },
    },
    {
      field: 'housingName',
      headerName: 'Housing',
      flex: 1,
      minWidth: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.row.housingName || <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}
        </Box>
      ),
    },
    {
      field: 'counselorFullName',
      headerName: 'Counselor',
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const name = `${params.row.counselorFirstName ?? ''} ${params.row.counselorLastName ?? ''}`.trim();
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {name || <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}
          </Box>
        );
      },
    },
  ], [picRefreshKey]);

  // Expanded client card: everything that used to live in the row's rich cells (insurance,
  // contact, full service history, notes & alerts) now lives here, modernized into a small
  // grid of bordered sections instead of stacked table-cell text.
  const getClientDetailPanelContent = (params) => {
    const row = params.row;
    const fullName = `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
    const counselorName = `${row.counselorFirstName ?? ''} ${row.counselorLastName ?? ''}`.trim();
    const sortedServices = filterRowServices(row?.service)
      .slice()
      .sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0));
    const recentServices = sortedServices.slice(0, 5);

    return (
      <Box sx={{ p: 2.5, bgcolor: '#FAFBFC', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 1.5 }}>
          <DetailSection icon={ReceiptOutlined} title="Insurance">
            <div>{row?.insurance?.name || '—'}</div>
            {row?.insurance?.name && (
              <>
                <KeyValueRow label="Group #" value={row.insurance?.insuredsGroup} />
                <KeyValueRow label="Insured's ID #" value={row.insurance?.insuredsIdNumber} />
              </>
            )}
          </DetailSection>

          <DetailSection icon={PhoneOutlinedIcon} title="Contact">
            <KeyValueRow label="Primary" value={row.primaryPhone} />
            <KeyValueRow label="Cell" value={row.cellPhone} />
          </DetailSection>

          <DetailSection
            icon={HotelIcon}
            title="Housing"
            actions={(row.housingName || row.housingId) ? [{ label: 'View clients', onClick: () => onHousingRowFilterClick(row.housingId) }] : null}
          >
            {row.housingName || '—'}
          </DetailSection>

          <DetailSection
            icon={PersonIcon}
            title="Counselor"
            actions={(row.counselorFirstName || row.counselorLastName) ? [{ label: 'View clients', onClick: () => onCounselorRowFilterClick(row.counselorId) }] : null}
          >
            {counselorName || '—'}
          </DetailSection>

          <DetailSection
            icon={CalendarMonthOutlined}
            title="Recent Services"
            actions={sortedServices.length > 0 ? [
              { label: 'Calendar', onClick: () => openCalendarForClient(row.id, fullName, null, 'clients') },
              { label: 'Statistics', onClick: () => openStatistics(row.id, fullName) },
            ] : null}
          >
            {sortedServices.length === 0 ? '—' : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                {recentServices.map((service) => (
                  <ServiceContainer key={service?.id}>
                    <ServiceHeader>
                      <ServiceDate>
                        {service?.startDate ? moment(service.startDate).format('MM/DD/YYYY') : ''}
                      </ServiceDate>
                      {String(service.status).toLowerCase() === 'billable' ? (
                        <Tooltip title="Billable" placement="top" arrow>
                          <AttachMoney sx={{ fontSize: 16, color: '#2e7d32', flexShrink: 0 }} />
                        </Tooltip>
                      ) : (
                        <ServiceStatus>{service.status}</ServiceStatus>
                      )}
                    </ServiceHeader>
                    <ServiceButtons style={{ textAlign: 'left' }}>
                      <ServiceName style={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}>
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: serviceColors[service.name] || '#ccc', marginRight: 6, flexShrink: 0 }} />
                        <Link component="button" onClick={() => handleServiceClick(row.id, service, row)} underline="hover" style={{ textAlign: 'left' }}>
                          {service.name}
                        </Link>
                      </ServiceName>
                    </ServiceButtons>
                  </ServiceContainer>
                ))}
                {sortedServices.length > recentServices.length && (
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                    +{sortedServices.length - recentServices.length} more
                  </Typography>
                )}
              </Box>
            )}
          </DetailSection>

          <DetailSection
            icon={WarningAmber}
            title="Notes & Alerts"
            actions={[
              { label: 'View all', onClick: () => handleOpenNotes(row.quickNotes, row.id) },
              { label: 'Calendar', onClick: () => openNotesCalendar(row.id, fullName) },
            ]}
          >
            {/* Actionable alerts render inside NotesGridField: intake-incomplete opens the
                intake form; "no active client assignments" opens the client on its
                Assignments section. */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5 }}>
            <NotesGridField
              types={types}
              notes={row.quickNotes}
              onAlertAction={(note, kind) => {
                if (kind === 'assignment') {
                  openClientAtTab(row.id, fullName, "222");
                  return;
                }
                if (kind === 'levelofcare') {
                  openClientAtTab(row.id, fullName, 0, 'levelOfCareId');
                  return;
                }
                return handleCreateIntakeService(row);
              }}
              onNoteClick={(note) => {
                setInitialEditNote(note);
                handleOpenNotes(row.quickNotes, row.id);
              }}
            />
            </Box>
          </DetailSection>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle="Client roster"
        icon={GroupsOutlinedIcon}
        accent="#1D9E75"
        rightContent={
          <>
            <CompactStatCard
              title="Intake Not Complete"
              value={intakeStatusCounts.Incomplete || 0}
              tone={(intakeStatusCounts.Incomplete || 0) > 0 ? 'error' : 'success'}
              icon={WarningAmber}
              isLoading={isLoading && rows.length === 0}
            />
            <CompactMetricCard
              title="Status" data={statusData} colorMap={statusColors}
              isLoading={isLoading && rows.length === 0}
              onClick={() => setMetricDialog({ title: 'Status', data: statusData, colorMap: statusColors })}
            />
            <Box
              onClick={() => setMetricsDialogOpen(true)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                bgcolor: 'rgba(255,255,255,0.72)', border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: 5, px: 1.75, py: 1,
                fontSize: 12, fontWeight: 700, color: '#0072E5',
                cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
              }}
            >
              <BarChartIcon sx={{ fontSize: 18 }} />
              View metrics
            </Box>
          </>
        }
      />
      <ClientMetricsDialog
        open={metricsDialogOpen}
        onClose={() => setMetricsDialogOpen(false)}
        levelOfCareTotals={levelOfCareTotals}
        getLevelOfCareIcon={getLevelOfCareIcon}
        housingData={housingData}
        housingColors={housingColors}
        counselorData={counselorData}
        counselorColors={counselorColors}
        isLoading={isLoading && rows.length === 0}
      />
      <MetricDashboardDialog
        open={!!metricDialog}
        onClose={() => setMetricDialog(null)}
        title={metricDialog?.title}
        data={metricDialog?.data}
        colorMap={metricDialog?.colorMap}
      />
    <Box sx={{ width: '100%', maxWidth: "calc(100% - 60px)", margin: "8px 30px 37px" }}>
      {isError && !isLoading && (
        <Box sx={{ mb: 2, p: 2, background: '#fde7e9', borderRadius: 1, color: '#a80000', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber fontSize="small" />
          <Typography variant="body2">
            Unable to connect to the server. Please check your connection and try again.
          </Typography>
          <Button size="small" variant="outlined" color="error" sx={{ ml: 'auto' }} onClick={() => {
            getClients({
              recordNumber: rowsPerPage,
              offset: page * rowsPerPage,
              status,
              counselor,
              housing,
              levelofcare,
              order,
              orderBy: replaceName(orderBy)
            });
          }}>
            Retry
          </Button>
        </Box>
      )}
      <StyledPaper sx={{ width: '100%', mb: 2 }}>
        <HeaderContainer>
          {/* Toolbar row: search + filter on left, buttons + sort on right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            {/* Left group */}
            <TableSearchContainer>
              <SearchInputContainer>
                <Search />
                <SearchInput
                  value={searched}
                  onChange={e => requestSearch(e.target.value)}
                  placeholder='Search'
                />
              </SearchInputContainer>
              <TableFilter onApplyFilters={onApplyFilters} onResetFilters={onResetFilters} />
            </TableSearchContainer>
            {/* Right group */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
              <CustomButton
                variant="contained"
                startIcon={<AddIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                onClick={(e) => setAddMenuAnchor(e.currentTarget)}
              >
                Add New
              </CustomButton>
              <Menu
                anchorEl={addMenuAnchor}
                open={!!addMenuAnchor}
                onClose={() => setAddMenuAnchor(null)}
              >
                <MenuItem onClick={() => { setAddMenuAnchor(null); setIsShowAddServicePopup(true); }}>
                  <MedicalServicesRounded fontSize="small" sx={{ mr: 1.5 }} />
                  Create Service
                </MenuItem>
                <MenuItem onClick={() => { setAddMenuAnchor(null); setIsShowEditNotePopup(true); }}>
                  <NoteAddRounded fontSize="small" sx={{ mr: 1.5 }} />
                  Add Note
                </MenuItem>
                <MenuItem onClick={() => { setAddMenuAnchor(null); setCreateClientOpen(true); }}>
                  <PersonAdd fontSize="small" sx={{ mr: 1.5 }} />
                  Add Client
                </MenuItem>
              </Menu>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SyncIcon />}
                onClick={() => {
                  setPicRefreshKey(k => k + 1);
                  getClients({
                    recordNumber: rowsPerPage,
                    offset: page * rowsPerPage,
                    status,
                    counselor,
                    housing,
                    levelofcare,
                    order,
                    orderBy: replaceName(orderBy),
                    searchVal: searched,
                  });
                }}
              >
                Refresh
              </Button>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="sort-by-label">Sort</InputLabel>
                <Select
                  labelId="sort-by-label"
                  id="clients-sort-select"
                  label="Sort"
                  value={sortSelect}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSortSelect(val);
                    let nextOrderBy = 'CreatedDate';
                    let nextOrder = 'desc';
                    if (val === 'CreatedDateAsc') { nextOrderBy = 'CreatedDate'; nextOrder = 'asc'; }
                    if (val === 'CreatedDateDesc') { nextOrderBy = 'CreatedDate'; nextOrder = 'desc'; }
                    if (val === 'ServicesStartAsc') { nextOrderBy = 'ServicesStart'; nextOrder = 'asc'; }
                    if (val === 'ServicesStartDesc') { nextOrderBy = 'ServicesStart'; nextOrder = 'desc'; }
                    if (val === 'ServicesEndAsc') { nextOrderBy = 'ServicesEnd'; nextOrder = 'asc'; }
                    if (val === 'ServicesEndDesc') { nextOrderBy = 'ServicesEnd'; nextOrder = 'desc'; }
                    setOrderBy(nextOrderBy);
                    setOrder(nextOrder);
                    getClients({
                      recordNumber: rowsPerPage,
                      offset: page * rowsPerPage,
                      status,
                      counselor,
                      housing,
                      levelofcare,
                      order: nextOrder,
                      orderBy: replaceName(nextOrderBy)
                    });
                  }}
                >
                  <MenuItem value={'CreatedDateDesc'}>Create Date Descending</MenuItem>
                  <MenuItem value={'CreatedDateAsc'}>Create Date Ascending</MenuItem>
                  <MenuItem value={'ServicesStartAsc'}>Services Start Ascending</MenuItem>
                  <MenuItem value={'ServicesStartDesc'}>Services Start Descending</MenuItem>
                  <MenuItem value={'ServicesEndAsc'}>Services End Ascending</MenuItem>
                  <MenuItem value={'ServicesEndDesc'}>Services End Descending</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </HeaderContainer>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <FilterChipList>
              {statues && status ?
                <FilterChip>
                  <span>Status: </span>{statues[status - 1]?.name}
                  <CrossImg src={crossSvg} alt="" onClick={onResetStatus} />
                </FilterChip> : null
              }
              {counselors && counselor ? (
                <FilterChip>
                  <span>Counselor: </span>
                  {counselors.find((c) => c.id === counselor)?.name || "Unknown"}
                  <CrossImg src={crossSvg} alt="" onClick={onResetCounselor} />
                </FilterChip>
              ) : null}
              {housings && housing ?
                <FilterChip>
                  <span>Housing: </span>{housings[housing - 1]?.name}
                  <CrossImg src={crossSvg} alt="" onClick={onResetHousing} />
                </FilterChip> : null
              }
              {levelofcares && levelofcare ?
                <FilterChip>
                  <span>Level of Care: </span>{levelofcares[levelofcare - 1]?.name}
                  <CrossImg src={crossSvg} alt="" onClick={onResetLevelofCare} />
                </FilterChip> : null
              }
            </FilterChipList>
          </Box>

          <div style={{ width: '100%', position: 'relative' }}>
            <DataGridPro
              autoHeight
              rows={rows}
              columns={gridColumns}
              getRowId={(row) => row.id}
              getRowHeight={() => 72}
              loading={isLoading}
              disableColumnMenu
              disableRowSelectionOnClick
              pagination
              paginationMode="server"
              rowCount={count}
              paginationModel={{ page, pageSize: rowsPerPage }}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[5, 10, 15, 25, 50]}
              getDetailPanelContent={getClientDetailPanelContent}
              getDetailPanelHeight={() => 'auto'}
              slots={{
                noRowsOverlay: () => (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EmptyMessage>No data to show</EmptyMessage>
                  </Box>
                ),
              }}
              sx={{
                backgroundColor: 'white',
                border: 'none',
                '& .MuiDataGrid-columnHeaders': { backgroundColor: 'white', borderColor: 'rgba(0,0,0,0.08)' },
                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                '& .MuiDataGrid-cell': { borderColor: 'rgba(0,0,0,0.06)' },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                '& .MuiDataGrid-row': { cursor: 'default' },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0,114,229,0.035)' },
                '& .MuiDataGrid-detailPanel': { borderColor: 'rgba(0,0,0,0.06)' },
              }}
            />
          </div>
        </Box>
      </StyledPaper>
      {/* ── Service info popup — unified SessionInfoDialog (≡ Calendar + Nav-bar bell) ── */}
      {selectedGridEvent && (() => {
        const { service, row: evtRow } = selectedGridEvent;
        const evtClientName = `${evtRow.firstName ?? ''} ${evtRow.lastName ?? ''}`.trim();
        // Enrich the lean grid service with the full record once it loads, so the popup shows
        // the same fields as the calendar (provider, time, attendance, billing, form).
        const detail = gridServiceDetail && gridServiceDetail.id === service.id ? gridServiceDetail : null;
        const startVal = detail?.startDate ?? service.startDate;
        const endVal = detail?.endDate ?? service.endDate ?? startVal;
        const providerName = detail?.owner
          ? `${detail.owner.firstName ?? ''} ${detail.owner.lastName ?? ''}`.trim()
          : null;
        const formId = detail?.psychosocialAssessmentFormId ?? service.psychosocialAssessmentFormId ?? null;
        // Map client-grid service shape → the session shape SessionInfoDialog expects
        const session = {
          title: detail?.name ?? service.name,
          start: startVal,
          end: endVal,
          // Prefer the service definition's flag; fall back to a midnight-start heuristic.
          allDay: serviceAllDayMap[String(detail?.serviceId ?? service.serviceId)]
            ?? (!startVal || dayjs(startVal).format('HH:mm') === '00:00'),
          serviceId: detail?.serviceId ?? service.serviceId,
          isEditable: service.isEditable ?? false,
          providerName: providerName || null,
          clients: [{
            id: service.id,
            clientId: evtRow.id,
            clientFirstName: detail?.clientFirstName ?? evtRow.firstName,
            clientLastName: detail?.clientLastName ?? evtRow.lastName,
            levelOfCare: evtRow.levelOfCareName ?? evtRow.levelofcare ?? null,
            attendanceStatus: detail?.attendanceStatus ?? service.attendanceStatus ?? null,
            attendanceStatusId: detail?.attendanceStatusId ?? null,
            serviceStatus: detail?.serviceStatus ?? service.serviceStatus ?? null,
            statusId: detail?.statusId ?? null,
            billingStatus: detail?.billingStatus ?? service.status ?? null,
            isEditable: service.isEditable ?? false,
            psychosocialAssessmentFormId: formId,
            psychosocialAssessmentFormVersion: service.psychosocialAssessmentFormVersion ?? null,
            user: detail?.owner
              ? { firstName: detail.owner.firstName, lastName: detail.owner.lastName }
              : (service.user ?? null),
            start: startVal,
            end: endVal,
            // Optimistic override so an attendance/perform change shows without a refetch.
            ...(gridServicePatch[service.id] ?? {}),
          }],
        };
        return (
          <SessionInfoDialog
            open
            session={session}
            SessionClientAvatar={SessionClientAvatar}
            attendanceOptions={gridAttendanceOptions}
            onSetAttendance={gridOnSetAttendance}
            onMarkPerformed={gridOnMarkPerformed}
            markPerformedBusyId={gridMarkPerformedBusyId}
            onViewCalendar={(sess) => {
              setSelectedGridEvent(null);
              openCalendarForClient(evtRow.id, evtClientName, sess?.start ?? startVal);
            }}
            onClose={() => { setSelectedGridEvent(null); setGridServicePatch({}); }}
            onOpenClient={(cid, cname) => { setSelectedGridEvent(null); openClient(cid, cname); }}
            onEditService={(c) => {
              // Keep the session dialog open behind the edit drawer (drawer z-index 1400 sits
              // above the dialog's 1300), mirroring the calendar's edit flow.
              setClientId(evtRow.id);
              setClientName(evtClientName);
              // IMPORTANT: the grid's service.serviceId is the PsychosocialAssessmentFormId
              // (aliased in GetClientList), NOT the real Service.Id — saving it violates the
              // ClientService→Service FK. Use the full record's serviceId (and other fields).
              setChangebleEvent({
                id: service.id,
                serviceId: detail?.serviceId ?? service.serviceId,
                startDate: detail?.startDate ?? service.startDate,
                endDate: detail?.endDate ?? service.endDate ?? service.startDate,
                description: detail?.description ?? service.name,
                clientId: evtRow.id,
                clientName: evtClientName,
                attendanceStatus: detail?.attendanceStatus ?? service.attendanceStatus,
                serviceGroupNameId: detail?.serviceGroupNameId ?? service.serviceGroupNameId ?? 0,
                serviceTopicId: detail?.serviceTopicId ?? service.serviceTopicId ?? 0,
                serviceRoomId: detail?.serviceRoomId ?? service.serviceRoomId ?? null,
                trackId: detail?.trackId ?? service.trackId ?? 0,
                note: detail?.note ?? service.note ?? '',
                clientServiceGroupId: detail?.clientServiceGroupId ?? null,
                clientServiceGroupCount: detail?.clientServiceGroupCount ?? null,
                // Provider/owner so the drawer shows the currently assigned provider (the drawer
                // resolves providerUserId directly, or falls back to providerName once users load).
                providerUserId: detail?.ownerId ?? null,
                providerName: detail?.owner
                  ? `${detail.owner.firstName ?? ''} ${detail.owner.lastName ?? ''}`.trim()
                  : null,
                psychosocialAssessmentForm: service.psychosocialAssessmentForm ?? null,
              });
              setIsShowAddServicePopup(true);
            }}
            onOpenClientForm={(c) => {
              setSelectedGridEvent(null);
              const tabTitle = evtClientName ? `${evtClientName} - ${service.name}` : service.name;
              openService(service.serviceId, tabTitle, evtRow.id, service.id, service.psychosocialAssessmentFormVersion ?? null);
            }}
            onViewHistory={(c) => setStatusHistoryServiceId(c.id)}
          />
        );
      })()}
      {gridPerformWarning}

      {/* Service status history — same grid dialog as the Calendar popup */}
      <ServiceHistoryDialog
        open={!!statusHistoryServiceId}
        onClose={() => setStatusHistoryServiceId(null)}
        title="Service Status History"
        rows={gridStatusHistory}
        isLoading={isGridHistoryLoading}
        isError={isGridHistoryError}
        emptyText="No status history for this service."
      />
      <Dialog open={isDetailPanelOpen} onClose={handleCloseDetailPanel} fullWidth maxWidth="md">
        <DialogTitle>
          Service Details
          <Button onClick={handleCloseDetailPanel} sx={{ position: "absolute", right: 16 }}>
            Close
          </Button>
        </DialogTitle>
        <DialogContent>
          {selectedService && (
            <Suspense fallback={<Skeleton variant="rounded" height={240} />}>
            <DetailPanelContent
              propClientId={selectedUserId}
              propFormId={selectedService.psychosocialAssessmentFormId || selectedService.serviceId}
              propClientServiceId={selectedService.id}
              propFormVersion={selectedService.psychosocialAssessmentFormVersion}
              onClose={handleCloseDetailPanel}
            />
            </Suspense>
          )}
        </DialogContent>
      </Dialog>
      {openNotesPanel && <NotesPanel setVisibility={(v) => { setOpenNotesPanel(v); if (!v) setInitialEditNote(null); }} clientIdForm={clientId} userNotes={notes} initialEditNote={initialEditNote} />}
      {(serviceCalendarOpen || notesCalendarOpen) &&
        <Suspense fallback={null}>
        <CalendarModalWrapper
          type={notesCalendarOpen ? 'notes' : 'services'}
          open={notesCalendarOpen ? openNotesCalendar : openServiceCalendar}
          onClose={notesCalendarOpen ? closeNotesCalendar : closeServiceCalendar}
          clientIdForm={selectedUserId}
          selectedUserName={selectedUserName}
        />
        </Suspense>
      }

      {serviceDrawerOpened && (
      <Suspense fallback={null}>
      <ServiceEditDrawer
        mode={changebleEvent ? "row" : "add"}
        initialValues={changebleEvent ?? {}}
        open={isShowAddServicePopup}
        onClose={onPopupClose}
        onSave={onCreateServiceWrapper}
        isSaving={isEditServiceLoading || isPostServiceLoading}
        onDelete={changebleEvent ? handleDeleteServiceFromDrawer : undefined}
        clients={assignmentClients}
        onClientsTypeChange={handleClientsTypeChange}
        clientsType={clientsType}
      />
      </Suspense>
      )}
      {notePopupOpened && (
      <Suspense fallback={null}>
      <AddNotePopup
        event={null}
        clientName={clientName}
        open={isShowEditNotePopup}
        onClose={onPopupClose}
        onClick={onCreateNote}
        clientId={null}
        clients={assignmentClients}
        clientsType={clientsType}
        onClientsTypeChange={handleClientsTypeChange}
      />
      </Suspense>
      )}
      <ServicesStatisticsPopup clientName={selectedUserName} clientId={selectedUserId} open={servicesStatisticsOpen} onClose={closeStatistics} />
      <Dialog open={createClientOpen} onClose={() => setCreateClientOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h2">Create Client</Typography>
          <IconButton aria-label="close" onClick={() => setCreateClientOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Suspense fallback={<Skeleton variant="rounded" height={320} />}>
            <CreateClientTab onClose={() => setCreateClientOpen(false)} />
          </Suspense>
        </DialogContent>
      </Dialog>
    </Box>
    </>
  );
}